import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { api, NotLoggedInError, type SessionUser } from './api/client.js';
import { Console } from './pages/Console.js';
import { Login } from './pages/Login.js';

/**
 * ทั้งแอปมีหน้าเดียว — ผังกำลังคนเต็มจอ
 *
 * ────────────────────────────────────────────────────────────────
 *  ⚠ เมนูเจ็ดอันกับ router ถูกยกออกจากที่นี่แล้ว
 * ────────────────────────────────────────────────────────────────
 *
 * เดิม App เป็นตัวสลับหน้าเจ็ดหน้าตาม hash และมีเมนูซ้ายเป็นตัวเดิน
 * ตอนนี้เหลือหน้าที่เดียวคือ "ใครเข้าอยู่ตอนนี้" แล้วส่งต่อให้ Console
 *
 * router.ts ยังอยู่ครบและยังทำงานได้ ไม่ได้ลบ เพราะมันเก็บเรื่องที่คิดมาแล้วไว้เยอะ
 * ทั้งการ encode id หน่วยงานที่มีช่องว่างกับเครื่องหมาย > และเหตุผลที่ใช้ hash ไม่ใช่ History API
 * วันที่จะเอาหน้าใดหน้าหนึ่งกลับมา ให้ import useRoute/navigate กลับเข้ามาที่นี่
 * แล้วเรนเดอร์ตามชื่อเส้นทาง — เส้นทางทุกชื่อยังถูกกำหนดไว้ครบใน router.ts
 * รายชื่อหน้าที่ยกออกไปทั้งหมดอยู่ในหัวคอมเมนต์ของ pages/Console.tsx
 */
export function App() {
  const queryClient = useQueryClient();

  // ถามเซิร์ฟเวอร์ว่ามีเซสชันอยู่ไหม ก่อนตัดสินใจว่าจะแสดงหน้าไหน
  // retry: false เพราะถ้ายังไม่ได้เข้าสู่ระบบ การถามซ้ำก็ได้คำตอบเดิม
  const session = useQuery({ queryKey: ['me'], queryFn: api.me, retry: false, staleTime: Infinity });
  const user = session.data ?? null;

  /**
   * เซสชันหมดอายุระหว่างใช้งาน — คำขอไหนก็ได้ที่ได้ 401 กลับมา
   * จะทำให้กลับไปหน้าเข้าสู่ระบบ พร้อมล้าง cache ที่ค้างอยู่ทิ้ง
   * ไม่งั้นพอเข้าใหม่จะเห็นข้อมูลเก่าของเซสชันก่อนหน้าค้างอยู่
   */
  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event.query.state.error instanceof NotLoggedInError) {
        queryClient.setQueryData(['me'], null);
      }
    });
    return unsubscribe;
  }, [queryClient]);

  /**
   * ล้าง cache ของทุกอย่าง แต่เก็บ ['me'] ไว้
   *
   * ห้ามใช้ queryClient.clear() ตรงนี้ — clear() ทิ้ง query object ของ ['me'] ไปด้วย
   * ทำให้ useQuery ที่ผูกอยู่หลุดจากตัวที่ setQueryData สร้างขึ้นใหม่ หน้าจอเลยไม่อัปเดตตาม
   * (อาการคือกดออกจากระบบแล้วยังเห็นแอปค้างอยู่ ทั้งที่ cookie ถูกลบไปแล้ว)
   */
  function dropCachedPages() {
    queryClient.removeQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
  }

  function onLoggedIn(next: SessionUser) {
    dropCachedPages();
    queryClient.setQueryData(['me'], next);
  }

  async function logout() {
    try {
      await api.logout();
    } finally {
      // ตั้ง ['me'] เป็น null ก่อน หน้าจอจะได้เด้งกลับไปหน้าเข้าสู่ระบบทันที
      // แล้วค่อยทิ้งข้อมูลที่เหลือ จะได้ไม่มีข้อมูลของเซสชันเก่าค้างให้คนถัดไปเห็น
      queryClient.setQueryData(['me'], null);
      dropCachedPages();
    }
  }

  if (session.isPending) {
    return <p className="state boot">กำลังตรวจสอบสิทธิ์การเข้าใช้งาน…</p>;
  }

  if (!user) {
    return <Login onSuccess={onLoggedIn} />;
  }

  return <Console user={user} onLogout={() => void logout()} />;
}
